import { toMap } from '@app/utils/common';
import { UserLevel } from '../models/user-level';
import { EnterpriseOption } from './enterprise-option';
import { OptionType } from './enterprise-option-definition';

export interface FeatureProfile {
  id?: number; // Option[Int] = None,
  externalId?: number; // Int,
  name?: string; // String,
  default: boolean; // Boolean = false
  description?: string; // String,
  resellerId?: number; // Int,
  maxCalls?: number; // Option[Int] = None,
  maxRegistrations?: number; // Option[Int] = None,
  maxSubscriptions?: number; // Option[Int] = None,
  maxDevices?: number; // Int = 1,
  createdOn?: Date; // Option[DateTime] = Some(DateTime.now),
  updatedOn?: Date; // DateTime = DateTime.now,
  features?: FeatureProfileFeature[]; // Seq[Feature] = Nil
  order?: number; // Option[Int] = None,
  userAddressBookFilterBitmask?: number;
}

export interface FeatureProfileFeature {
  id?: number; // Option[Int] = None,
  name: string; // String,
  description?: string; // Option[String] = None,
  defaultJson?: any; // Option[JsValue] = None,
  virtual: boolean; // Boolean,
  licensed: boolean; // Boolean,
  dependsOn?: string; // Option[String] = None,
  blockedBy?: string; // Option[String] = None,
  // In combination with feature profile it has additional fields
  optional?: boolean; // Option[Boolean] = Some(false),
  resellerAccess?: AuthUserFeatureAccess; // Option[AuthUserFeatureAccess.Value] = Some(AuthUserFeatureAccess.MANAGE)
  enterpriseAdminAccess?: AuthUserFeatureAccess; // Option[AuthUserFeatureAccess.Value] = Some(AuthUserFeatureAccess.MANAGE)
  preview?: boolean; // Option[Boolean] = Some(false),
  deprecated?: boolean; // Option[Boolean] = Some(false),
  // and in context of a specific user and an optional feature we have following
  userOptionalFeatureActive?: boolean; // Option[Boolean] = Some(false)
}

export enum AuthUserFeatureAccess {
  NONE = 'NONE',
  VIEW = 'VIEW',
  MANAGE = 'MANAGE',
}

/**
 * System admin gets always the right to manage.
 * Reseller ... based on the setting for that feature if it is set to `MANAGE`.
 * Same for an enterprise admin.
 * End-User does not get the right to manage.
 *
 * @param f Feature from the feature-profile
 * @param userLevel auth-user role level
 * @returns
 */
export function isOptionalFeatureManageableByUserLevel(
  f: FeatureProfileFeature,
  userLevel: UserLevel
): boolean {
  if (userLevel === UserLevel.SystemAdmin) {
    return true;
  } else if (userLevel === UserLevel.ServiceProviderAdmin) {
    return f.resellerAccess === AuthUserFeatureAccess.MANAGE;
  } else if (userLevel === UserLevel.EnterpriseAdmin) {
    return f.enterpriseAdminAccess === AuthUserFeatureAccess.MANAGE;
  }
  return false;
}

export const FeatureNameToEnterpriseOptionName = {
  AD_HOC_CONFERENCING: 'AdHocConferenceFeatures',
  CALL_CENTER_AGENT: 'CallCenterAgents',
  CALL_CENTER_PREMIUM_AGENT: 'CallCenterAgents',
  CALL_CENTER_SUPERVISOR: 'CallCenterSupervisors',
  CALL_REC_ADHOC: 'CallRecService',
  CALL_REC_PERMANENT: 'CallRecPermanentFeatures',
  CSTA_3RD_PARTY: 'CstaFeatures',
  CSTA_THIRD_PARTY_CALL_CONTROL: 'CstaFeatures',
  DESKTOP_COMMUNICATOR: 'DesktopCommunicator',
  DESKTOP_CTI: 'DesktopCti',
  DESKTOP_OPERATOR: 'DesktopOperator',
  FAX_MAIL: 'FaxmailFeatures',
  MICROSOFT_TEAMS: 'MicrosoftTeamsUsers',
  MOBILE_COMMUNICATOR: 'MobileCommunicator',
  MOBILE_OFFICE: 'MobileOffice',
  MS_365_PRESENCE: 'Microsoft365PresenceSyncFeatures',
  RICH_PRESENCE: 'RichPresenceService',
  SINGLE_NUMBER_REACH: 'SingleNumberFeatures',
  UC_COLLABORATION: 'CollaborationFeatures',
  UC_INSTANT_MESSAGING: 'InstantMessagingFeatures',
  VOICE_MAIL: 'VoicemailFeatures',
  VOICE_RECORDING: 'CallRecPermanentFeatures',
  WEB_COMMUNICATOR: 'WebCommunicator',
};

/**
 * Filters out user optional features based on available enterprise options and they current state.
 * @param fs feature-profile-features
 * @param eos enterprise options
 * @param havingAvailableLicense default `false`.
 * The result includes all features that do have an enterprise option that is active or has a count greater then zero.
 * Set this flag to `true` if you also want to filter out those with depleted licenses
 * (concerns only "COUNT" enterprise options).
 * @returns
 */
export function filterOptionalFeaturesBasedOnEnterpriseOptions(
  fs: FeatureProfileFeature[],
  eos: EnterpriseOption[],
  havingAvailableLicense: boolean = false
): FeatureProfileFeature[] {
  const eoMap = toMap(eos, 'name');
  const filteredFeatures = [];
  fs.forEach((f) => {
    if (f.optional === true) {
      const eoName = FeatureNameToEnterpriseOptionName[f.name];
      const eo = eoMap[eoName];
      if (eo == null) {
        if (eoName && !f.userOptionalFeatureActive) {
          console.debug('Ignore feature ' + f.name + ' for missing enterprise option ' + eoName, f);
        } else {
          filteredFeatures.push(f);
        }
      } else {
        if (eo.definition?.optionType === OptionType.BOOL) {
          if (eo.active === true) {
            filteredFeatures.push(f);
          } else {
            console.debug('enterprise option', eo.name, 'is inactive for the feature', f.name);
          }
        } else if (eo.definition?.optionType === OptionType.COUNT) {
          if (eo.active && eo.count > 0) {
            if (havingAvailableLicense === true) {
              if ((eo.used ?? 0) < (eo.available ?? 0)) {
                filteredFeatures.push(f);
              }
            } else {
              filteredFeatures.push(f);
            }
          } else {
            console.debug(
              'enterprise option',
              eo.name,
              'is inactive or has zero count for the feature',
              f.name
            );
          }
        } else {
          console.error('Could not check the enterprise option for the feature', eo, f);
        }
      }
    } else {
      filteredFeatures.push(f);
    }
  });
  return filteredFeatures;
}

/**
 * AssignedFeatureProfile represents the management information of a specific Feature Profile on an enterprise.
 * Such as, information of how many are available (`count`), and how many are actually `used`.
 * A feature profile can also be a "default" feature profile (specified on service provider) and those are unbounded in usage.
 * Default feature profiles can be identified with the expression `assignedFeatureProfile.definition.default === true`.
 */
export interface AssignedFeatureProfile {
  id?: number; // Option[Int] = None,
  enterpriseId: number; // Int,
  featureProfileId: number; // Int,
  name: string; // String,
  count?: number; // Option[Int] = None,
  used?: number; // Option[Int] = None,
  createdOn?: Date; // DateTime = DateTime.now(),
  updatedOn?: Date; // DateTime = DateTime.now(),
  definition?: FeatureProfile; // Option[FeatureProfile] = None
}
